/* UV Load landing-page interactivity. Scoped to .uv-load-section elements only. */
(function () {
  'use strict';

  function initFaq(root) {
    var triggers = root.querySelectorAll('.uvl-faq-trigger');
    if (!triggers.length) return;
    var prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function closePanel(panel, trigger, animated) {
      trigger.setAttribute('aria-expanded', 'false');
      panel.removeAttribute('data-state');
      if (animated && !prefersReduced) {
        // Animate from current pixel height down to 0 so transition runs.
        panel.style.maxHeight = panel.scrollHeight + 'px';
        // Force a reflow so the browser commits the starting height.
        panel.offsetHeight; // eslint-disable-line no-unused-expressions
        panel.style.maxHeight = '0px';
        var onEnd = function (e) {
          if (e.propertyName !== 'max-height') return;
          panel.removeEventListener('transitionend', onEnd);
          panel.setAttribute('hidden', '');
          panel.style.maxHeight = '';
        };
        panel.addEventListener('transitionend', onEnd);
      } else {
        panel.setAttribute('hidden', '');
        panel.style.maxHeight = '';
      }
    }

    function openPanel(panel, trigger) {
      trigger.setAttribute('aria-expanded', 'true');
      panel.removeAttribute('hidden');
      panel.setAttribute('data-state', 'open');
      if (prefersReduced) {
        panel.style.maxHeight = 'none';
      } else {
        panel.style.maxHeight = '0px';
        panel.offsetHeight; // eslint-disable-line no-unused-expressions
        panel.style.maxHeight = panel.scrollHeight + 'px';
        var onEnd = function (e) {
          if (e.propertyName !== 'max-height') return;
          panel.removeEventListener('transitionend', onEnd);
          panel.style.maxHeight = 'none';
        };
        panel.addEventListener('transitionend', onEnd);
      }
    }

    triggers.forEach(function (trigger) {
      var panelId = trigger.getAttribute('aria-controls');
      var panel = panelId ? root.querySelector('#' + panelId) : null;
      if (!panel) return;

      // Initial state: collapsed unless markup says otherwise.
      var initiallyOpen = trigger.getAttribute('aria-expanded') === 'true';
      if (initiallyOpen) {
        panel.removeAttribute('hidden');
        panel.setAttribute('data-state', 'open');
        panel.style.maxHeight = 'none';
      } else {
        panel.setAttribute('hidden', '');
      }

      trigger.addEventListener('click', function () {
        var isOpen = trigger.getAttribute('aria-expanded') === 'true';
        // Close all peers first (mutually-exclusive accordion).
        triggers.forEach(function (peer) {
          if (peer === trigger) return;
          var peerPanelId = peer.getAttribute('aria-controls');
          var peerPanel = peerPanelId ? root.querySelector('#' + peerPanelId) : null;
          if (peerPanel && peer.getAttribute('aria-expanded') === 'true') {
            closePanel(peerPanel, peer, true);
          }
        });
        if (isOpen) {
          closePanel(panel, trigger, true);
        } else {
          openPanel(panel, trigger);
        }
      });
    });
  }

  /* Klaviyo waitlist — when the embed replaces the form with its success
     message, wrap the role="status" node in a pill that matches the form's
     visual footprint and append an Instagram CTA link. The link is a
     SIBLING of the live region (never injected inside it) so screen
     readers announce only Klaviyo's original text and don't re-announce
     when we mutate. */
  function initWaitlist(root) {
    var containers = root.querySelectorAll('.uvl-klaviyo');
    if (!containers.length) return;

    var INSTAGRAM_HREF = 'https://www.instagram.com/pelotan.cc/';
    var INSTAGRAM_LABEL = 'Follow @pelotan.cc on Instagram (opens in new tab)';
    // Outline glyph: rounded square + lens + corner dot. currentColor inherits
    // from the button so the same SVG works on light (navy bg) and dark (white bg).
    var INSTAGRAM_SVG =
      '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" ' +
      'fill="none" stroke="currentColor" stroke-width="2" ' +
      'stroke-linecap="round" stroke-linejoin="round">' +
      '<rect x="3" y="3" width="18" height="18" rx="5" ry="5"/>' +
      '<circle cx="12" cy="12" r="4"/>' +
      '<circle cx="17.5" cy="6.5" r="0.6" fill="currentColor" stroke="none"/>' +
      '</svg>';

    function isErrorNode(node) {
      if (!node || node.nodeType !== 1) return false;
      if (node.matches('[role="alert"], [data-testid*="error"]')) return true;
      var cls = node.className && node.className.toString ? node.className.toString() : '';
      return /error/i.test(cls);
    }

    function isSuccessNode(node) {
      if (!node || node.nodeType !== 1) return false;
      if (isErrorNode(node)) return false;
      if (!node.matches('[role="status"], [class*="success"], [data-testid*="success"]')) return false;
      // Klaviyo briefly renders empty status shells during validation.
      return !!(node.textContent && node.textContent.trim());
    }

    function buildInstagramLink(doc) {
      var a = doc.createElement('a');
      a.className = 'uvl-instagram-btn';
      a.href = INSTAGRAM_HREF;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.setAttribute('aria-label', INSTAGRAM_LABEL);
      a.innerHTML = INSTAGRAM_SVG;
      return a;
    }

    function wrapSuccess(container, statusNode) {
      // Idempotent: bail if the pill already exists in this container.
      if (container.querySelector('.uvl-success-pill')) return;
      var doc = container.ownerDocument || document;
      var pill = doc.createElement('div');
      pill.className = 'uvl-success-pill';
      // Insert pill in place of the status node, then move the status node
      // into it. One reparenting of the live region; we never alter its
      // contents, so the announcement text stays exactly what Klaviyo emitted.
      statusNode.parentNode.insertBefore(pill, statusNode);
      pill.appendChild(statusNode);
      pill.appendChild(buildInstagramLink(doc));
    }

    function findExistingSuccess(container) {
      var nodes = container.querySelectorAll(
        '[role="status"], [class*="success"], [data-testid*="success"]'
      );
      for (var i = 0; i < nodes.length; i++) {
        if (isSuccessNode(nodes[i])) return nodes[i];
      }
      return null;
    }

    containers.forEach(function (container) {
      if (container.dataset.uvlWaitlistInit === '1') return;
      container.dataset.uvlWaitlistInit = '1';

      // Edge case: success state already in DOM on load (e.g. cached state).
      var existing = findExistingSuccess(container);
      if (existing) {
        wrapSuccess(container, existing);
        return;
      }

      var observer = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var added = mutations[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var node = added[j];
            if (node.nodeType !== 1) continue;
            if (isSuccessNode(node)) {
              wrapSuccess(container, node);
              observer.disconnect();
              return;
            }
            // Klaviyo may insert the success node inside an outer wrapper.
            if (node.querySelector) {
              var inner = findExistingSuccess(node);
              if (inner) {
                wrapSuccess(container, inner);
                observer.disconnect();
                return;
              }
            }
          }
        }
      });
      observer.observe(container, { childList: true, subtree: true });
    });
  }

  function init() {
    var sections = document.querySelectorAll('.uv-load-section');
    sections.forEach(function (section) {
      initFaq(section);
      initWaitlist(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run on Shopify section reload (theme editor live reload events).
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.classList && event.target.classList.contains('uv-load-section')) {
      initFaq(event.target);
      initWaitlist(event.target);
    }
  });
})();
